import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarClock, Plus } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useCreatePayRun, usePayRuns, usePaySchedules } from '@/features/payroll/queries'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'
import type { PayRun } from '@/features/payroll/types'

const STATUS_VARIANT: Record<string, 'success' | 'secondary' | 'danger' | 'warning' | 'outline' | 'info'> = {
  draft: 'outline',
  calculating: 'info',
  review: 'warning',
  approved: 'info',
  posted: 'success',
  paid: 'success',
  reversed: 'secondary',
  failed: 'danger',
  cancelled: 'secondary',
}

export function PayRunsListPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const [statusFilter, setStatusFilter] = useState('')
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    pay_schedule_id: '',
    period_start: '',
    period_end: '',
    pay_date: '',
    run_type: 'regular',
  })

  const { data, isLoading } = usePayRuns({ status: statusFilter || undefined })
  const { data: schedules } = usePaySchedules()
  const createMut = useCreatePayRun()
  const canCreate = hasPermission('payroll.runs.create')

  const columns = useMemo(
    () => [
      {
        accessorKey: 'run_number',
        header: 'Run',
        cell: ({ row }: { row: { original: PayRun } }) => (
          <Button
            variant="link"
            onClick={() => navigate(`/payroll/runs/${row.original.id}`)}
            className="h-auto justify-start p-0 font-medium"
          >
            {row.original.run_number}
          </Button>
        ),
      },
      {
        accessorKey: 'pay_date',
        header: 'Pay date',
        cell: ({ row }: { row: { original: PayRun } }) => formatDate(row.original.pay_date),
      },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }: { row: { original: PayRun } }) =>
          `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end)}`,
      },
      {
        accessorKey: 'run_type',
        header: 'Type',
        cell: ({ row }: { row: { original: PayRun } }) => (
          <Badge variant="outline">{row.original.run_type.replace(/_/g, ' ')}</Badge>
        ),
      },
      {
        accessorKey: 'employee_count',
        header: 'Employees',
        cell: ({ row }: { row: { original: PayRun } }) => row.original.employee_count,
      },
      {
        accessorKey: 'gross_minor_units',
        header: 'Gross',
        cell: ({ row }: { row: { original: PayRun } }) =>
          formatPayrollMoney(row.original.gross_minor_units, row.original.currency),
      },
      {
        accessorKey: 'net_minor_units',
        header: 'Net',
        cell: ({ row }: { row: { original: PayRun } }) =>
          formatPayrollMoney(row.original.net_minor_units, row.original.currency),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: PayRun } }) => (
          <Badge variant={STATUS_VARIANT[row.original.status] || 'secondary'}>
            {row.original.status}
          </Badge>
        ),
      },
    ],
    [navigate],
  )

  async function onSave() {
    if (!form.period_start || !form.period_end || !form.pay_date) {
      toast({
        title: 'Required fields missing',
        description: 'Period start, period end, and pay date are required.',
        variant: 'destructive',
      })
      return
    }
    try {
      const r = await createMut.mutateAsync({
        pay_schedule_id: form.pay_schedule_id || null,
        period_start: form.period_start,
        period_end: form.period_end,
        pay_date: form.pay_date,
        run_type: form.run_type,
        currency: 'USD',
      })
      toast({ title: 'Pay run created', description: r.data.run_number })
      setOpen(false)
      setForm({ pay_schedule_id: '', period_start: '', period_end: '', pay_date: '', run_type: 'regular' })
    } catch (err) {
      toast({
        title: 'Create failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    }
  }

  return (
    <Page
      title="Pay Runs"
      description="Draft, calculate, approve, and post pay runs. Each posted run produces a snapshot-driven, immutable payslip and posts balanced entries to the GL."
      actions={
        canCreate ? (
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-2 size-4" /> New pay run
          </Button>
        ) : undefined
      }
    >
      <Toolbar align="start">
        <FormField
          label=""
          control={
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="approved">Approved</option>
              <option value="posted">Posted</option>
              <option value="paid">Paid</option>
              <option value="reversed">Reversed</option>
            </select>
          }
        />
      </Toolbar>
      <DataTable
        columns={columns as never}
        rows={data?.data ?? []}
        loading={isLoading}
        empty={
          <EmptyState
            icon={CalendarClock}
            title="No pay runs yet"
            description="Create your first pay run. You can preview, recalculate, and approve before posting to the ledger."
            action={
              canCreate ? (
                <Button onClick={() => setOpen(true)}>
                  <Plus className="mr-2 size-4" /> New pay run
                </Button>
              ) : undefined
            }
          />
        }
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="New pay run"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} disabled={createMut.isPending}>
              {createMut.isPending ? 'Creating…' : 'Create draft'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Pay schedule (optional)"
            control={
              <select
                value={form.pay_schedule_id}
                onChange={(e) => setForm({ ...form, pay_schedule_id: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Off-cycle (no schedule)</option>
                {(schedules?.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} - {s.frequency}
                  </option>
                ))}
              </select>
            }
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField
              label="Period start"
              required
              type="date"
              value={form.period_start}
              onChange={(e) => setForm({ ...form, period_start: e.target.value })}
            />
            <FormField
              label="Period end"
              required
              type="date"
              value={form.period_end}
              onChange={(e) => setForm({ ...form, period_end: e.target.value })}
            />
            <FormField
              label="Pay date"
              required
              type="date"
              value={form.pay_date}
              onChange={(e) => setForm({ ...form, pay_date: e.target.value })}
            />
          </div>
          <FormField
            label="Run type"
            control={
              <select
                value={form.run_type}
                onChange={(e) => setForm({ ...form, run_type: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="regular">Regular</option>
                <option value="off_cycle">Off-cycle</option>
                <option value="bonus">Bonus</option>
                <option value="correction">Correction</option>
                <option value="final_settlement">Final settlement</option>
                <option value="retro">Retro</option>
              </select>
            }
          />
        </div>
      </Modal>
    </Page>
  )
}
